import React from 'react'

import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'

import { Icon, Text } from '@mxenabled/mxui'

import Mercury from './Mercury'

import { useWidgetContainerProvider } from '../../../common'
import { type DetailedBudget } from '../../../core'
import type { BubbleData } from '../BubbleChart'

const LARGE_THRESHOLD = 75
const MEDIUM_THRESHOLD = 50

interface BubbleProps {
  bubble: BubbleData
  isDraggable: boolean
  onClick?: (budget: DetailedBudget) => void
}

export function Bubble({ bubble, isDraggable, onClick = () => {} }: BubbleProps) {
  const [showTooltip, setShowTooltip] = React.useState(false)
  const theme = useTheme()

  const { availableWidth } = useWidgetContainerProvider()

  const budgetBubble = React.useMemo(() => {
    if (!bubble) return undefined
    return {
      amount: bubble.amount,
      budgetColors: bubble.budgetColors,
      guid: bubble.guid,
      radius: bubble.radius,
      transaction_total: bubble.transaction_total,
    }
  }, [bubble.amount, bubble.budgetColors, bubble.guid, bubble.radius, bubble.transaction_total])

  if (!bubble) return undefined

  const {
    budgetColors: { background, text: textColor },
    category: { icon, name },
    guid,
    description,
    radius,
    x,
    y,
  } = bubble

  const defaultSvgOffset = availableWidth < 400 ? -8 : -12

  let iconX = defaultSvgOffset
  let iconY = defaultSvgOffset

  if (radius > LARGE_THRESHOLD) {
    iconX = -14
    iconY = -45
  } else if (radius > MEDIUM_THRESHOLD) {
    iconY = -32
  }

  let iconDimension = 32

  if (radius <= LARGE_THRESHOLD) {
    iconDimension = availableWidth < 400 ? 16 : 24
  }

  const tooltipText = `${bubble.category.name}: ${description}`
  const tooltipPopperSlot = {
    // Screen readers already get this content in focus order from the <g>
    // aria-label. The tooltip is purely visual, so hide its portaled node from
    // assistive tech to keep it from being announced detached from its trigger
    // (and with nothing for mobile screenreader users to dismiss).
    'aria-hidden': true,
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, 15] },
      },
    ],
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick(bubble)
    }
  }

  const handleFocus = () => {
    setShowTooltip(true)
  }

  const handleBlur = () => {
    setShowTooltip(false)
  }

  return (
    <g
      aria-label={`${name} - ${description}`}
      className="bubble"
      id={`bubble-${guid}`}
      key={guid}
      onBlur={handleBlur}
      onClick={() => onClick(bubble)}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      role="button"
      style={{ cursor: isDraggable ? 'pointer' : 'default', touchAction: 'manipulation' }}
      tabIndex={0}
      textAnchor="middle"
      x={x}
      y={y}
    >
      <style>
        {`
          .bubble:focus,
          .bubble:focus-visible {
            outline: none;
          }
          .bubble:focus circle,
          .bubble:focus-visible circle {
            stroke: ${theme.palette.primary.main};
            stroke-width: 3;
            stroke-opacity: 0.8;
          }
        `}
      </style>
      <Tooltip open={showTooltip} slotProps={{ popper: tooltipPopperSlot }} title={tooltipText}>
        <circle fill={background} id={`circle-${guid}`} r={radius} />
      </Tooltip>

      <svg x={iconX} y={iconY}>
        <Icon name={icon} size={iconDimension} sx={{ fill: textColor }} />
      </svg>

      {radius > LARGE_THRESHOLD && (
        <Text
          bold={true}
          component="text"
          id={`budget-category-${guid}`}
          sx={{ fill: textColor }}
          variant="body2"
          /* @ts-expect-error Property doesn't exist on type */
          y={4}
        >
          {name}
        </Text>
      )}

      {radius > MEDIUM_THRESHOLD && (
        <Text
          bold={true}
          component="text"
          id={`budget-description-${guid}-1`}
          sx={{ fill: textColor }}
          variant="body1"
          /* @ts-expect-error Property doesn't exist on type */
          y={radius <= LARGE_THRESHOLD ? 12 : 24}
        >
          {radius <= LARGE_THRESHOLD ? description.split(' ')[0] : description}
        </Text>
      )}

      {radius <= LARGE_THRESHOLD && radius > MEDIUM_THRESHOLD && (
        <Text
          component="text"
          id={`budget-description-${guid}-2`}
          sx={{ fill: textColor }}
          variant="caption"
          /* @ts-expect-error Property doesn't exist on type */
          y={28}
        >
          {description.split(' ')[1]}
        </Text>
      )}

      <Mercury bubble={budgetBubble as BubbleData} />
    </g>
  )
}
